import { useSelector } from 'react-redux';
import { SlideOutSheet } from '../../../components/SlideOutSheet';
import { FundingPolicyType, SessionType, UNDERWRITING_STATUS_CONFIG } from '@shared';
import Divider from '@mui/material/Divider';
import { parseUnderwritingGuidanceResponse } from '@shared';
import { Button, Spinner } from '@design-system';
import { RefreshCw } from 'lucide-react';
import { selectSession } from '@client/model';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { StructuredSlideOutContent } from './structured-slide-out-content';
import { useRerunUnderwriting } from '../hooks/use-rerun-underwriting';

interface UnderwritingSlideOutProps {
  open: boolean;
  onClose: () => void;
  underwritingResult: string | null | undefined;
  structuredResult?: StructuredUnderwritingResult | null;
  pipelineMetadata?: UnderwritingPipelineMetadata | null;
  productName: string;
  fundingPolicyType?: FundingPolicyType;
}

export function UnderwritingSlideOut({
  open,
  onClose,
  underwritingResult,
  structuredResult,
  pipelineMetadata,
  productName,
  fundingPolicyType,
}: UnderwritingSlideOutProps) {
  const session = useSelector(selectSession);
  const isWizard = session.sessionType === SessionType.wizard;
  const { flags } = useFeatureFlags();
  const canRerun = !!flags.structuredUnderwriting && !!fundingPolicyType;
  const getStatusBadge = (status: string) => {
    // Map status labels to config keys
    const statusKey = Object.keys(UNDERWRITING_STATUS_CONFIG).find(
      key =>
        UNDERWRITING_STATUS_CONFIG[key as keyof typeof UNDERWRITING_STATUS_CONFIG].label === status,
    ) as keyof typeof UNDERWRITING_STATUS_CONFIG | undefined;

    if (!statusKey) {
      return (
        <span className="rounded-full bg-yellow-100 p-2 text-xs font-medium text-yellow-800">
          {status}
        </span>
      );
    }

    const config = UNDERWRITING_STATUS_CONFIG[statusKey];
    return (
      <span
        className={`rounded-full p-2 text-xs font-medium ${config.classes.bgColor} ${config.classes.textColor}`}
      >
        {status}
      </span>
    );
  };

  const parsedResults =
    !structuredResult && underwritingResult
      ? parseUnderwritingGuidanceResponse(underwritingResult)
      : null;

  // Legacy rendering path
  const legacyContent = parsedResults ? (
    <div className="space-y-6">
      {/* Disclaimer */}
      <div className="rounded-lg bg-gray-50 p-4">
        <div className="text-primary-dark text-xs">
          <p className="mb-2">
            <span className="font-semibold">Disclaimer:</span> This analysis is generated by a
            Wealth Assurance Group AI tool, <span className="font-semibold">not</span> by the
            insurance carrier. It is for informational purposes only and does not guarantee your
            final underwriting outcome.
          </p>
          <ul className="ml-4 list-disc space-y-1 pl-2">
            <li className="pl-1">
              <span className="font-semibold">AI can make mistakes.</span> You are responsible for
              verifying these insights.
            </li>
            <li className="pl-1">
              <span className="font-semibold">This is not an offer of coverage.</span> All final
              eligibility and pricing decisions are made exclusively by the carrier's underwriters.
            </li>
            <li className="pl-1">
              <span className="font-semibold">These insights are not advice.</span> They are
              informational observations based upon what is known by Wealth Assurance Group about
              the applicant and the carrier's eligibility guidelines, it is not professional
              insurance, financial, medical, or legal advice.
            </li>
          </ul>
        </div>
      </div>

      {/* Eligibility Overview Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <h3 className="text-primary-dark text-lg font-medium">Eligibility Overview</h3>
          {getStatusBadge(parsedResults.eligibilityOverview.status)}
        </div>

        <div className="space-y-3">
          {parsedResults.eligibilityOverview.content.map((item, index) => (
            <p
              key={index}
              className="text-primary-dark text-sm"
            >
              {item}
            </p>
          ))}
        </div>
      </div>
      <Divider />

      {/* Flagged Items Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <h3 className="text-primary-dark text-lg font-medium">Flagged Items</h3>
          {!parsedResults.flaggedItems[0]?.includes('None') && (
            <span
              className={`rounded-full p-2 text-xs font-medium ${UNDERWRITING_STATUS_CONFIG.unsure.classes.bgColor} ${UNDERWRITING_STATUS_CONFIG.unsure.classes.textColor}`}
            >
              {UNDERWRITING_STATUS_CONFIG.unsure.label}
            </span>
          )}
        </div>
        {parsedResults.flaggedItems.length > 0 ? (
          <ul className="space-y-3">
            {parsedResults.flaggedItems.map((item, index) => {
              const hasSource = item.includes('Source:');
              const parts = hasSource ? item.split('Source:') : [item];
              return (
                <li
                  key={index}
                  className="flex items-start gap-4"
                >
                  <span className="text-primary-dark h-2 w-2 flex-shrink-0 rounded-full">•</span>
                  <div className="flex flex-col gap-1">
                    <span className="text-primary-dark text-sm">{parts[0].trim()}</span>
                    {hasSource && (
                      <>
                        <Divider />
                        <span className="text-xs italic text-gray-700">Source:{parts[1]}</span>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <div>None</div>
        )}
      </div>
      <Divider />

      {/* Recommendations Section */}
      <div className="space-y-4">
        <h3 className="text-primary-dark text-lg font-medium">Recommendations</h3>
        {parsedResults.recommendations.length > 0 ? (
          <ul className="space-y-3">
            {parsedResults.recommendations.map((item, index) => (
              <li
                key={index}
                className="flex items-start gap-4"
              >
                <span className="text-primary-dark h-2 w-2 flex-shrink-0 rounded-full">•</span>
                <span className="text-primary-dark text-sm">{item}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div>None</div>
        )}
      </div>
    </div>
  ) : null;

  const slideOutContent = structuredResult ? (
    <StructuredSlideOutContent structuredResult={structuredResult} pipelineMetadata={pipelineMetadata} />
  ) : legacyContent ?? (
    <div className="flex flex-col items-center justify-center gap-3 py-8">
      <p className="text-gray-700">No underwriting analysis available</p>
      {canRerun && <EmptyStateRerunButton fundingPolicyType={fundingPolicyType!} />}
    </div>
  );

  const isLegacyResult = !structuredResult && !!underwritingResult;

  const slideOutContentWithHeader = (
    <div className="pb-16">
      {/* Product name header */}
      <div className="flex items-center justify-between gap-2 border-b border-gray-200 pb-2">
        <h2 className="text-primary-dark text-lg font-medium">{productName}</h2>
        {isWizard && canRerun && (
          <WizardRerunButton fundingPolicyType={fundingPolicyType!} />
        )}
      </div>

      {isLegacyResult && canRerun && (
        <LegacyPipelineBanner fundingPolicyType={fundingPolicyType!} />
      )}

      <div className="mt-3">
        {slideOutContent}
      </div>
    </div>
  );

  return (
    <SlideOutSheet
      open={open}
      setOpen={open => !open && onClose()}
      title="Underwriting Analysis"
      slideOutContent={slideOutContentWithHeader}
      maxWidth="max-w-md"
      noMargin
    />
  );
}

function WizardRerunButton({ fundingPolicyType }: { fundingPolicyType: FundingPolicyType }) {
  const { rerun, isRunning } = useRerunUnderwriting(fundingPolicyType);

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={rerun}
      disabled={isRunning}
      className="h-7 gap-1.5 text-xs"
    >
      {isRunning ? (
        <>
          <Spinner className="h-3 w-3" />
          Re-running…
        </>
      ) : (
        <>
          <RefreshCw className="h-3 w-3" />
          Re-run
        </>
      )}
    </Button>
  );
}

function LegacyPipelineBanner({ fundingPolicyType }: { fundingPolicyType: FundingPolicyType }) {
  const { rerun, isRunning } = useRerunUnderwriting(fundingPolicyType);

  return (
    <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
      <p className="text-sm text-blue-900">
        This analysis was generated by a previous version of our AI. For the most accurate
        assessment, we recommend re-running the analysis.
      </p>
      <Button
        onClick={rerun}
        disabled={isRunning}
        variant="link"
        size="sm"
        className="mt-1 h-auto gap-1.5 p-0 text-blue-700 hover:text-blue-900"
      >
        {isRunning ? (
          <>
            <Spinner className="h-3 w-3" />
            Re-running analysis…
          </>
        ) : (
          <>
            <RefreshCw className="h-3 w-3" />
            Re-run analysis
          </>
        )}
      </Button>
    </div>
  );
}

function EmptyStateRerunButton({ fundingPolicyType }: { fundingPolicyType: FundingPolicyType }) {
  const { rerun, isRunning } = useRerunUnderwriting(fundingPolicyType);

  return (
    <Button
      variant="default"
      size="sm"
      onClick={rerun}
      disabled={isRunning}
      className="gap-1.5"
    >
      {isRunning ? (
        <>
          <Spinner className="h-3 w-3" />
          Running analysis…
        </>
      ) : (
        <>
          <RefreshCw className="h-3 w-3" />
          Run analysis
        </>
      )}
    </Button>
  );
}
